import { ChevronRight, Footprints } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCount } from '@/utils/number';
import type { JourneyOverlay } from '../hooks/useMoTravelDetail';
import { countJourneyStops, type JourneyEntry } from '../utils/journeyEntries';
import type { StartPlace } from '../utils/startPlaces';
import { MoJourneyStepper } from './MoJourneyStepper';
import { MoStartPlacePicker } from './MoStartPlacePicker';

const OVERLAY_POSITION_CLASS = 'absolute bottom-3 left-3 z-10';

interface MoJourneyOverlayProps {
  overlay: JourneyOverlay;
  journeyEntries: JourneyEntry[];
  startPlaces: StartPlace[];
  focusedKey: string | null;
  canReturnToStartPicker: boolean;
  onStepSelect: (waypointKey: string) => void;
  onStartHover: (startKey: string | null) => void;
  onJourneyStart: () => void;
  onJourneyExit: () => void;
}

export function MoJourneyOverlay({
  overlay,
  journeyEntries,
  startPlaces,
  focusedKey,
  canReturnToStartPicker,
  onStepSelect,
  onStartHover,
  onJourneyStart,
  onJourneyExit,
}: MoJourneyOverlayProps) {
  if (overlay === 'stepper') {
    return (
      <MoJourneyStepper
        entries={journeyEntries}
        activeKey={focusedKey}
        onStepSelect={onStepSelect}
        onExit={onJourneyExit}
        canReturnToStartPicker={canReturnToStartPicker}
        className={OVERLAY_POSITION_CLASS}
      />
    );
  }

  if (overlay === 'startButton') {
    return (
      <Button
        variant="outline"
        onClick={onJourneyStart}
        className={cn(
          OVERLAY_POSITION_CLASS,
          'group h-auto gap-2.5 rounded-lg bg-card/95 py-1.5 pr-2.5 pl-1.5 shadow-md backdrop-blur-sm hover:bg-card',
        )}
      >
        <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm">
          <Footprints className="size-4" aria-hidden />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-sm font-semibold">Start journey</span>
          <span className="text-2xs font-normal text-muted-foreground">
            {formatCount(countJourneyStops(journeyEntries), 'stop')}
          </span>
        </span>
        <ChevronRight
          className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </Button>
    );
  }

  if (overlay === 'startPicker') {
    return (
      <MoStartPlacePicker
        startPlaces={startPlaces}
        onStartSelect={onStepSelect}
        onStartHover={onStartHover}
        className={OVERLAY_POSITION_CLASS}
      />
    );
  }

  if (overlay === 'multiDayHint') {
    return (
      <p
        className={cn(
          OVERLAY_POSITION_CLASS,
          'flex max-w-[calc(100%-4.5rem)] items-center gap-1.5 rounded-lg border bg-background/95 px-2.5 py-1.5 text-xs text-muted-foreground shadow-sm backdrop-blur-sm',
        )}
      >
        <Footprints className="size-3.5 shrink-0" aria-hidden />
        Click a stop, or pick a day in the date picker, to step through its journey
      </p>
    );
  }

  return null;
}
